import { useEffect, useMemo, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useSettingsStore } from '@/stores/settingsStore'

interface CodeInputProps {
  value: string
  onChange: (value: string) => void
  /** Fired once the last digit lands — lets the form submit without a click. */
  onComplete?: (value: string) => void
  length?: number
  disabled?: boolean
  invalid?: boolean
  autoFocus?: boolean
  label?: string
}

const onlyDigits = (raw: string) => raw.replace(/\D/g, '')

/**
 * One box per digit of the emailed code.
 *
 * Handles the three things people actually do with these: type, paste the whole
 * code (from any box), and backspace their way back. The boxes are a presentation
 * detail — the value the form sees is always a plain string of digits.
 */
export function CodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  invalid = false,
  autoFocus = false,
  label,
}: CodeInputProps) {
  const resolvedTheme = useSettingsStore((s) => s.resolvedTheme)
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const digits = useMemo(
    () => Array.from({ length }, (_, index) => value[index] ?? ''),
    [value, length],
  )

  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus()
  }, [autoFocus])

  function focusAt(index: number) {
    const clamped = Math.max(0, Math.min(length - 1, index))
    inputsRef.current[clamped]?.focus()
    inputsRef.current[clamped]?.select()
  }

  function commit(next: string) {
    const trimmed = next.slice(0, length)
    onChange(trimmed)
    if (trimmed.length === length) onComplete?.(trimmed)
  }

  function handleInput(index: number, raw: string) {
    const typed = onlyDigits(raw)
    if (!typed) {
      // Deleting the character in a box clears just that position.
      const cleared = digits.map((digit, i) => (i === index ? '' : digit)).join('')
      onChange(cleared.trimEnd())
      return
    }
    // Typing more than one digit (autofill, fast paste) fills forward from here.
    const next = [...digits]
    typed.split('').forEach((digit, offset) => {
      if (index + offset < length) next[index + offset] = digit
    })
    commit(next.join(''))
    focusAt(index + typed.length)
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault()
      const next = [...digits]
      next[index - 1] = ''
      onChange(next.join('').trimEnd())
      focusAt(index - 1)
      return
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusAt(index - 1)
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusAt(index + 1)
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = onlyDigits(event.clipboardData.getData('text'))
    if (!pasted) return
    event.preventDefault()
    commit(pasted)
    focusAt(pasted.length)
  }

  const borderColor = invalid
    ? 'var(--color-danger-border)'
    : resolvedTheme === 'dark'
      ? 'rgba(148,163,184,.28)'
      : 'rgba(148,163,184,.45)'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {label ? (
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</span>
      ) : null}
      <div
        style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}
        role="group"
        aria-label={label}
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputsRef.current[index] = element
            }}
            value={digit}
            onChange={(event) => handleInput(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={handlePaste}
            onFocus={(event) => event.target.select()}
            disabled={disabled}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            aria-label={`${index + 1}`}
            maxLength={1}
            style={{
              width: '100%',
              minWidth: 0,
              height: 54,
              textAlign: 'center',
              fontSize: 24,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--text)',
              background: 'var(--bg-input)',
              border: `1px solid ${borderColor}`,
              borderRadius: 10,
              outline: 'none',
              transition: 'border-color .15s ease, box-shadow .15s ease',
              opacity: disabled ? 0.6 : 1,
            }}
          />
        ))}
      </div>
    </div>
  )
}
